import * as React from 'react';
import { Link } from 'react-router-dom';

import { Input } from '@built/design-system';
import { ReactComponent as LockIcon } from '@built/design-system/icons/action/lock.svg';

import * as styles from './EmailField.module.scss';
import { getValidationError } from '../utils';
import l10n from '@built/i18n';
import { ValidationErrorType } from 'src/types';

/**
 * Renders an email field with a validation indicator
 */
export default function EmailField(props: EmailFieldProps) {
  const { className, showError, showSsoLink, ssoUrl, languageReady } = props;
  const classes = [] as string[];
  let subtext = '';
  let inputLabel = languageReady
    ? l10n.t('EmailField:info.label', 'Email Address')
    : 'Email Address';

  if (className) {
    classes.push(className);
  }

  // if user has provided some input and it's invalid
  // we need to toggle the error state for the EmailField
  // and add some subtext to prompt the user
  if (showError) {
    classes.push(styles.error);
    subtext = getValidationError(ValidationErrorType.VALIDATION_EMAIL_ERROR);
  }

  return (
    <React.Fragment>
      {/* Generic SSO login UX renders the LoginSso component */}
      {showSsoLink && !ssoUrl && (
        <Link
          to={'login-sso'}
          className={styles.ssoLink}
          onClick={props.trackLoginSSO}
        >
          <LockIcon />{' '}
          {l10n.t(
            'EmailField:info.SingleSign-on',
            'Log in with Single Sign-On'
          )}
        </Link>
      )}

      {/* External SSO login UX simply redirects the browser to the right spot */}
      {showSsoLink && ssoUrl && (
        <a href={ssoUrl} className={styles.ssoLink}>
          <LockIcon />{' '}
          {l10n.t(
            'EmailField:info.SingleSign-on',
            'Log in with Single Sign-On'
          )}
        </a>
      )}

      <Input
        className={classes.join(' ')}
        disabled={props.disabled}
        autoFocus={props.autoFocus}
        label={inputLabel}
        name={props.name}
        onChange={props.onChange}
        size="lg"
        value={props.value}
        subtext={subtext}
      />
    </React.Fragment>
  );
}

export interface EmailFieldProps {
  /** An additional class name to provide custom styling */
  className?: string;

  /** Whether the input should be disabled */
  disabled?: boolean;

  /** Whether the input should be focused as soon as it is rendered */
  autoFocus?: boolean;

  /** Name of the EmailField input element */
  name: string;

  showError?: boolean;

  /** EmailField input value */
  value: string;

  /** Is the email address valid? */
  validEmail: boolean;

  /** Controls if we allow showing the single sign-on link */
  showSsoLink?: boolean;

  /** If provided, we'll send the user to the provided URL for auth */
  ssoUrl?: string;

  /** Flag to determine if translations are ready */
  languageReady: boolean;

  /** Callback to handle updates to the current email value */
  onChange(event: React.SyntheticEvent<HTMLInputElement>): void;

  /** Track snowplow event when a user clicks "Login with SSO" */
  trackLoginSSO?(): void;
}
